import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import {
  CEP_CHANGED_EVENT,
  formatCep,
  getStoredCep,
  OPEN_CEP_POPUP_EVENT,
} from '@theme/cep-session';
import { estimateProductShipping, renderShippingRateItems } from '@theme/shipping-estimator';

export const SHIPPING_STATUS_EVENT = 'petyard:shipping-status';

/**
 * @typedef {object} ShippingCalculatorRefs
 * @property {HTMLElement} loadingState
 * @property {HTMLElement} ratesList
 * @property {HTMLElement} unavailableState
 * @property {HTMLElement} missingCepState
 * @property {HTMLButtonElement} [cepChip]
 * @property {HTMLElement} [cepChipLabel]
 */

class ShippingCalculator extends Component {
  /** @type {number} */
  #requestId = 0;

  /** @type {boolean} */
  #isEstimating = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #estimateTimeout = null;

  /** @type {string | null} */
  #variantId = null;

  /** @type {number} */
  #quantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#variantId = this.dataset.variantId || null;
    this.#quantity = Number(this.dataset.quantityDefault) || 1;

    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.addEventListener(CEP_CHANGED_EVENT, this.#handleCepChanged);

    this.#updateCepChip();
    this.#scheduleEstimate();
  }

  disconnectedCallback() {
    clearTimeout(this.#estimateTimeout);
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener(CEP_CHANGED_EVENT, this.#handleCepChanged);
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const variant = detail?.resource;
        if (!variant?.id) return;

        const nextVariantId = String(variant.id);
        if (nextVariantId === this.#variantId) return;

        this.#variantId = nextVariantId;
        this.#scheduleEstimate();
      })
      .catch(() => {});
  };

  #handleCepChanged = () => {
    this.#updateCepChip();
    if (this.#isEstimating) return;
    this.#scheduleEstimate();
  };

  #updateCepChip() {
    const chip = this.refs.cepChip;
    const label = this.refs.cepChipLabel;
    const cep = getStoredCep();

    if (!(chip instanceof HTMLElement) || !(label instanceof HTMLElement)) return;

    if (!cep) {
      chip.setAttribute('hidden', '');
      label.textContent = '';
      return;
    }

    label.textContent = formatCep(cep);
    chip.removeAttribute('hidden');
  }

  #scheduleEstimate() {
    if (this.#estimateTimeout) {
      clearTimeout(this.#estimateTimeout);
    }

    this.#estimateTimeout = setTimeout(() => {
      this.#estimateTimeout = null;
      this.#runEstimate();
    }, 150);
  }

  /**
   * @param {'loading' | 'rates' | 'unavailable' | 'missing_cep'} state
   */
  #setView(state) {
    this.dataset.view = state;
  }

  /**
   * @param {'loading' | 'available' | 'unavailable' | 'missing_cep'} status
   * @param {import('@theme/shipping-estimator').ShippingEstimateResult} [result]
   */
  #unavailablePayload() {
    return {
      product_brand: this.dataset.itemBrand || '',
      item_id: this.dataset.itemId || '',
      item_name: this.dataset.itemName || '',
    };
  }

  #dispatchStatus(status, result) {
    const detail = {
      status,
      rates: result?.rates || [],
      productId: this.dataset.productId,
      ...this.#unavailablePayload(),
    };

    document.dispatchEvent(
      new CustomEvent(SHIPPING_STATUS_EVENT, {
        bubbles: true,
        composed: true,
        detail,
      })
    );

    if (status !== 'unavailable') return;

    const payload = this.#unavailablePayload();
    if (window.petyardGtm && typeof window.petyardGtm.unavailableForDelivery === 'function') {
      window.petyardGtm.unavailableForDelivery(payload);
    }

    this.dispatchEvent(
      new CustomEvent('petyard:unavailable-for-delivery', {
        bubbles: true,
        composed: true,
        detail: payload,
      })
    );
  }

  /**
   * @param {import('@theme/shipping-estimator').ShippingEstimateResult} result
   */
  #renderRates(result) {
    const list = this.refs.ratesList;
    if (!list) return;
    renderShippingRateItems(list, result.rates, 'shipping-calculator');
  }

  async #runEstimate() {
    const requestId = ++this.#requestId;

    if (!getStoredCep()) {
      if (requestId !== this.#requestId) return;
      this.#setView('missing_cep');
      this.#dispatchStatus('missing_cep');
      return;
    }

    if (!this.#variantId) {
      return;
    }

    const productForm = this.closest('.shopify-section')?.querySelector('product-form-component');
    const qtyInput = productForm?.querySelector('[name="quantity"]');
    if (qtyInput instanceof HTMLInputElement) {
      this.#quantity = Number(qtyInput.value) || 1;
    }

    if (requestId !== this.#requestId) return;

    this.#setView('loading');
    this.#dispatchStatus('loading');
    this.#isEstimating = true;

    try {
      const result = await estimateProductShipping(this.#variantId, this.#quantity);
      if (requestId !== this.#requestId) return;

      if (!result.available) {
        this.#setView('unavailable');
        this.#dispatchStatus('unavailable', result);
        return;
      }

      this.#renderRates(result);
      this.#setView('rates');
      this.#dispatchStatus('available', result);
    } catch {
      if (requestId !== this.#requestId) return;
      this.#setView('unavailable');
      this.#dispatchStatus('unavailable');
    } finally {
      if (requestId === this.#requestId) {
        this.#isEstimating = false;
      }
    }
  }

  openCepPopup() {
    document.dispatchEvent(new CustomEvent(OPEN_CEP_POPUP_EVENT));
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}